import React, { useEffect, useState } from 'react';
import { View, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { polygonService } from '../../services/central-api/polygons';
import { translate } from '../../services/translate';
import MyAppText from '../MyAppText';
import CheckboxList from './CheckboxList';
import RadioList, { FilterListItem } from './RadioList';
import { FieldValues, Path, useController, useFormContext } from 'react-hook-form';
import { useCurrentApp } from '../../context/CurrentApp';
import FilterDependencyMessage from '../Filters/FilterDependencyMessage';

function RegionsList<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>
>({ name, companyId }: { name: TName; companyId?: number | null; }) {
    const { isParentApp } = useCurrentApp();
    const { styles, theme } = useStyles(styleSheet);

    const { control } = useFormContext<TFieldValues>();

    const { field } = useController({
        control,
        name,
    });

    const value = field.value as {
        type: string;
        value: number | number[];
    };

    const [typeItems, setTypeItems] = useState<FilterListItem<string>[]>([]);
    const [typeIsLoading, setTypeIsLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [polygonItems, setPolygonItems] = useState<FilterListItem[]>([]);
    const [polygonsIsLoading, setPolygonsIsLoading] = useState(false);

    async function getPolygonsType() {
        try {
            setTypeIsLoading(true);
            const polygonsType = await polygonService.getPolygonsType(isParentApp && companyId ? companyId : undefined);
            setTypeItems(polygonsType.map((polygon) => ({
                label: polygon.type,
                value: polygon.type
            })));
        } catch (err) {
            console.error(err);
        } finally {
            setTypeIsLoading(false);
        }
    }

    async function getPolygonsByType(polygonType: string) {
        try {
            setPolygonsIsLoading(true);
            const resp = await polygonService.getPolygonsByType(
                polygonType,
                isParentApp && companyId ? companyId : undefined,
            );
            setPolygonItems(resp.map((polygon) => ({ label: polygon.name, value: polygon.id })));
        } catch (err) {
            console.error(err);
        } finally {
            setPolygonsIsLoading(false);
        }
    }

    useEffect(() => {
        if (isParentApp && !companyId) {
            return;
        }
        getPolygonsType();

        if (value.type) {
            getPolygonsByType(value.type);
        }
    }, [companyId]);

    const toggleType = (type: string) => {
        const next = value.type === type ? '' : type;
        if (Array.isArray(value.value)) {
            field.onChange({
                type: next,
                value: [],
            });
        } else {
            field.onChange({
                type: next,
                value: 0,
            });
        }

        if (next) {
            getPolygonsByType(next);
        }
    };

    const filtered = typeItems.filter(item =>
        item.label.toLowerCase().includes(search.toLowerCase())
    );

    if (isParentApp && !companyId) {
        return <FilterDependencyMessage message={translate('selectCompanyFirst')} />;
    }

    return (
        <View style={styles.container}>
            {typeItems.length >= 7 && (
                <View style={styles.searchContainer}>
                    <FontAwesomeIcon icon={faMagnifyingGlass as IconDefinition} fontSize={16} color={theme.colors.neutral['300']} />
                    <TextInput
                        style={styles.searchInput}
                        placeholder={translate('typeToSearch')}
                        placeholderTextColor={theme.colors.neutral['300']}
                        value={search}
                        onChangeText={setSearch}
                    />
                </View>
            )}

            <View style={styles.countRow}>
                <MyAppText style={styles.countText}>
                    {translate('items')}: {filtered.length}
                </MyAppText>
                <TouchableOpacity onPress={() => {
                    field.onChange({
                        type: '',
                        value: Array.isArray(value.value) ? [] : 0,
                    });
                }} disabled={value.type === ''}>
                    <MyAppText style={[styles.clearSelection, value.type === '' && styles.clearSelectionDisabled]}>
                        {translate('clearSelection')}
                    </MyAppText>
                </TouchableOpacity>
            </View>

            {typeIsLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size='small' color={theme.colors.primary['500']} />
                </View>
            ) : (
                <ScrollView style={styles.list} contentContainerStyle={{ gap: 8 }} nestedScrollEnabled>
                    {filtered.map((type) => {
                        const isExpanded = value.type === type.value;
                        return (
                            <View key={type.value}>
                                <TouchableOpacity style={styles.typeRow} onPress={() => toggleType(type.value)}>
                                    <View style={styles.typeLeft}>
                                        <View style={[styles.radio, isExpanded && styles.radioSelected]}>
                                            {isExpanded && <View style={styles.radioInner} />}
                                        </View>
                                        <MyAppText style={styles.typeLabel}>{type.label}</MyAppText>
                                    </View>
                                    <FontAwesomeIcon
                                        icon={(isExpanded ? faChevronUp : faChevronDown) as IconDefinition}
                                        fontSize={12}
                                        color={theme.colors.primary['500']}
                                    />
                                </TouchableOpacity>

                                {isExpanded && (
                                    <View style={styles.subRegionsContainer}>
                                        {Array.isArray(value.value)
                                            ? <CheckboxList
                                                name={`${name}.value`}
                                                items={polygonItems}
                                                isLoading={polygonsIsLoading}
                                            />
                                            : <RadioList
                                                name={`${name}.value`}
                                                items={polygonItems}
                                                isLoading={polygonsIsLoading}
                                                clearValue={0}
                                                hideClearValue={true}
                                            />
                                        }
                                    </View>
                                )}
                            </View>
                        );
                    })}
                </ScrollView>
            )}
        </View>
    );
}

RegionsList.getCount = (value: {
    type: string;
    value: number | number[];
}) => {
    if (Array.isArray(value.value)) {
        return value.value.length;
    }
    return value.value !== 0 ? 1 : 0;
};

export default RegionsList;

const styleSheet = createStyleSheet((theme) => ({
    container: {
        gap: 12,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 16,
        color: theme.colors.neutral['400'],
        outlineStyle: 'none',
    },
    countRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    countText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.labelColor,
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    clearSelectionDisabled: {
        opacity: 0.4,
    },
    loadingContainer: {
        paddingVertical: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    list: {
        maxHeight: 320,
    },
    typeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    typeLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    radio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    radioSelected: {
        borderColor: theme.colors.primary['500'],
    },
    radioInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.primary['500'],
    },
    typeLabel: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    subRegionsContainer: {
        paddingLeft: 34,
        paddingTop: 12,
    },
}));
